import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function DetailReservationViaEmail({ reference }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!reference) {
      setError("Référence de réservation manquante");
      setLoading(false);
      return;
    }

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/reservation/details.php?ref=${reference}`,
      { 
        method: "GET", 
        headers: { "Content-Type": "application/json" },
        mode: "cors"
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Erreur HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((result) => {
        if (result.error) {
          setError(result.error);
        } else {
          setData(result);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Erreur chargement réservation:", err);
        setError("Impossible de charger la réservation. Veuillez réessayer.");
        setLoading(false);
      });
  }, [reference]);

  // Helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr; // au cas où la date est déjà lisible
    return d.toLocaleString("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return "N/A";
    return duration
      .replace("PT", "")
      .replace("H", "h ")
      .replace("M", "min");
  };

  const handlePrint = () => {
    const printContents = document.getElementById("print-zone").innerHTML;
    const win = window.open("", "", "width=800,height=600");
    win.document.write(printContents);
    win.document.close();
    win.print();
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex justify-center items-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <span className="text-blue-700 text-lg font-semibold">Chargement de votre réservation...</span>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex justify-center items-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
            <p className="text-red-600 mb-6">{error}</p>
          </div>
          <button
            onClick={() => navigate("/")}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div
          id="print-zone"
          className="bg-white shadow-2xl rounded-2xl overflow-hidden"
        >
          {/* Header moderne avec gradient */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-8 py-10 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold mb-2">
                  ✈️ Confirmation de Réservation
                </h1>
                <p className="text-emerald-100 text-lg">
                  Djater Voyage - Votre partenaire de confiance
                </p>
              </div>
              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                  <p className="text-sm text-emerald-100 mb-1">Référence</p>
                  <p className="text-2xl font-mono font-bold">{data.reference || "N/A"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Infos réservation avec cards modernes */}
          <section className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                📋
              </span>
              Informations de la Réservation
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-gray-600 mb-1">Code PNR</p>
                <p className="text-xl font-bold text-blue-800 font-mono">
                  {data.pnr_code || "En attente"}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                <p className="text-sm text-gray-600 mb-1">Date de Création</p>
                <p className="text-lg font-semibold text-purple-800">
                  {formatDate(data.created_at)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                <p className="text-sm text-gray-600 mb-1">Statut Réservation</p>
                <p className={`text-lg font-bold ${
                  data.reservation_status === "confirmée"
                    ? "text-green-600"
                    : data.reservation_status === "en_attente"
                    ? "text-orange-600"
                    : "text-blue-600"
                }`}>
                  {data.reservation_status === "confirmée" ? "✅ Confirmée" :
                   data.reservation_status === "en_attente" ? "⏳ En attente" :
                   data.reservation_status === "en_cours" ? "🔄 En cours" :
                   data.reservation_status || "N/A"}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
                <p className="text-sm text-gray-600 mb-1">Contact</p>
                <p className="text-sm font-semibold text-indigo-800 truncate">
                  📧 {data.customer_email}
                </p>
                <p className="text-sm font-semibold text-indigo-800">
                  📞 {data.customer_phone}
                </p>
              </div>
            </div>
          </section>

          {/* Passagers avec design moderne */}
          <section className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                👥
              </span>
              Liste des Passagers
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {data.passenger_data?.map((p, i) => {
                const travelerType = p.travelerType === 'ADULT' ? 'Adulte' : 
                                    (p.travelerType === 'CHILD' ? 'Enfant' : 'Bébé');
                return (
                  <div key={`passenger-${i}`} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 flex items-center">
                          <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3">
                            {i + 1}
                          </span>
                          {p.name?.firstName || ""} {p.name?.lastName || ""}
                        </h3>
                        <p className="text-sm text-blue-600 font-semibold mt-1">
                          {travelerType} • {p.gender === 'MALE' ? '👨 Homme' : p.gender === 'FEMALE' ? '👩 Femme' : p.gender}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                        <p className="text-xs text-gray-500">Date de Naissance</p>
                        <p className="font-semibold text-gray-800">{p.dateOfBirth || "N/A"}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">📧 Email</p>
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {p.contact?.emailAddress || "-"}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">📞 Téléphone</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {p.contact?.phones?.[0]?.countryCallingCode
                            ? `+${p.contact.phones[0].countryCallingCode} ${p.contact.phones[0].number}`
                            : "-"}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">🌍 Nationalité</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {p.documents?.[0]?.nationality || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">🪪 Document de Voyage</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {p.documents?.[0]?.documentType || "PASSPORT"} : {p.documents?.[0]?.number || "-"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Vols avec design moderne */}
          <section className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                ✈️
              </span>
              Détails des Vols
            </h2>
            <div className="space-y-6">
              {data.flight_data?.map((f, i) =>
                f.itineraries?.map((it, j) => (
                  <div key={`itinerary-${i}-${j}`} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                    {/* Header de l'itinéraire */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
                      <h3 className="text-lg font-bold">
                        Itinéraire {j + 1} • Durée totale: {formatDuration(it.duration)}
                      </h3>
                    </div>
                    {/* Segments */}
                    <div className="divide-y divide-gray-200">
                      {it.segments?.map((seg, k) => {
                        const depDate = new Date(seg.departure?.at);
                        const arrDate = new Date(seg.arrival?.at);
                        const baggageQty = seg.fareDetailsBySegment?.[0]?.includedCheckedBags?.quantity || 0;
                        
                        return (
                          <div key={`segment-${i}-${j}-${k}`} className="p-6 hover:bg-blue-50 transition-colors">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-4">
                                <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                  Vol {k + 1}
                                </div>
                                <div className="text-2xl font-bold text-gray-800">
                                  {seg.departure?.iataCode} → {seg.arrival?.iataCode}
                                </div>
                              </div>
                              <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-bold">
                                {seg.carrierCode} {seg.number}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                                <p className="text-xs text-gray-600 mb-1">🛫 Départ</p>
                                <p className="font-bold text-gray-800">{seg.departure?.iataCode}</p>
                                <p className="text-sm text-gray-600">
                                  {depDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                </p>
                                <p className="text-lg font-bold text-green-700">
                                  {depDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              
                              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                                <p className="text-xs text-gray-600 mb-1">🛬 Arrivée</p>
                                <p className="font-bold text-gray-800">{seg.arrival?.iataCode}</p>
                                <p className="text-sm text-gray-600">
                                  {arrDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                </p>
                                <p className="text-lg font-bold text-orange-700">
                                  {arrDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              
                              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                                <p className="text-xs text-gray-600 mb-1">✈️ Avion</p>
                                <p className="font-bold text-gray-800">{seg.aircraft?.code || "-"}</p>
                                <p className="text-xs text-gray-600 mt-1">⏱️ Durée</p>
                                <p className="font-bold text-purple-700">{formatDuration(seg.duration)}</p>
                              </div>
                              
                              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
                                <p className="text-xs text-gray-600 mb-1">🛋️ Classe</p>
                                <p className="font-bold text-gray-800">{seg.cabin || "Economy"}</p>
                                <p className="text-xs text-gray-600 mt-1">🧳 Bagages</p>
                                <p className="font-bold text-indigo-700">{baggageQty} pc(s)</p>
                              </div>
                            </div>
                            
                            {seg.co2Emissions?.[0] && (
                              <div className="mt-4 bg-green-50 rounded-lg p-3 border border-green-200">
                                <p className="text-sm text-gray-700">
                                  🌱 Émissions CO₂: <span className="font-bold text-green-700">
                                    {seg.co2Emissions[0].weight} kg
                                  </span>
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          

          
        </div>

        {/* Boutons d'action modernes */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
          <button
            onClick={() => navigate("/")}
            className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 font-bold text-lg"
          >
            <span className="text-2xl">🏠</span>
            Retour à l'accueil
          </button>
          <button
            onClick={handlePrint}
            aria-label="Imprimer la réservation"
            className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 font-bold text-lg"
          >
            <span className="text-2xl">🖨️</span>
            Imprimer la Réservation
          </button>
        </div>
      </div>
    </div>
  );
}
